import { DeliveryType, PaymentType } from "./Enums.js";
import { CustomerOverride, resolveCustomerOverride } from "./CustomerOverride.js";
/**
 * The payment options a sales zone can offer, in the order they are preferred when one
 * has to be picked automatically (online first, on invoice last).
 */
export const PAYMENT_TYPES = [
    PaymentType.online,
    PaymentType.offline,
    PaymentType.invoice,
];
/**
 * Anything that is not one of the three known payment types is no payment type at all.
 */
export function asPaymentType(value) {
    for (let paymentType of PAYMENT_TYPES) {
        if (value === paymentType) {
            return paymentType;
        }
    }
    return null;
}
/**
 * The payment options that really apply: the zone's, with the three overrides a logged-in
 * customer may carry. This says nothing about the company wide cash sales consent — a
 * customer can open up a way to pay for itself, it can't waive that consent.
 */
export function resolvePaymentOptions(zone, customer) {
    return {
        enableOnlinePayment: resolveCustomerOverride(zone ? zone.enableOnlinePayment : false, customer ? customer.enableOnlinePayment : null),
        enableOfflinePayment: resolveCustomerOverride(zone ? zone.enableOfflinePayment : false, customer ? customer.enableOfflinePayment : null),
        enableInvoicePayment: resolveCustomerOverride(zone ? zone.enableInvoicePayment : false, customer ? customer.enableInvoicePayment : null),
    };
}
/**
 * Whether the sales zone offers this way of paying at all, taking a logged-in customer's
 * own overrides into account.
 */
export function isPaymentTypeEnabledInZone(paymentType, zone, customer) {
    if (!zone) {
        return false;
    }
    let options = resolvePaymentOptions(zone, customer);
    if (paymentType == PaymentType.online) {
        return options.enableOnlinePayment;
    }
    if (paymentType == PaymentType.offline) {
        return options.enableOfflinePayment;
    }
    if (paymentType == PaymentType.invoice) {
        return options.enableInvoicePayment;
    }
    return false;
}
/**
 * On site payment additionally needs the company wide cash sales consent. Paying on
 * invoice is not a cash sale, so it stands on its own — just like online payment.
 */
export function isPaymentTypeAllowedByCompany(paymentType, companySettings) {
    if (paymentType == PaymentType.offline) {
        return Boolean(companySettings && companySettings.enableOfflinePayments);
    }
    return Boolean(asPaymentType(paymentType));
}
/**
 * The payment options a customer really gets to choose from, in preference order.
 *
 * Pass the delivery type to also apply the rule that deliveries are not paid on site:
 * they are prepaid online or billed on invoice. A zone that offers nothing but on site
 * payment keeps offering it, otherwise it could not take delivery orders at all.
 */
export function getAvailablePaymentTypes(zone, companySettings, deliveryType, customer) {
    let paymentTypes = PAYMENT_TYPES.filter((paymentType) => {
        return isPaymentTypeEnabledInZone(paymentType, zone, customer) && isPaymentTypeAllowedByCompany(paymentType, companySettings);
    });
    if (deliveryType == DeliveryType.delivery && paymentTypes.length > 1) {
        let withoutOnSitePayment = paymentTypes.filter((paymentType) => paymentType != PaymentType.offline);
        if (withoutOnSitePayment.length > 0) {
            paymentTypes = withoutOnSitePayment;
        }
    }
    return paymentTypes;
}
/**
 * Whether this way of paying may be used for the given zone/delivery type combination.
 */
export function isPaymentTypeAvailable(paymentType, zone, companySettings, deliveryType, customer) {
    let knownPaymentType = asPaymentType(paymentType);
    if (!knownPaymentType) {
        return false;
    }
    return getAvailablePaymentTypes(zone, companySettings, deliveryType, customer).includes(knownPaymentType);
}
